import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import {
  applyBatteryConcertApplicationStatus,
  invalidateBatteryConcertApplication,
} from "./battery-concert-application-cache"
import {
  CONCERT_DOCUMENT_ACCEPT,
  CONCERT_DOCUMENT_MAX_BYTES,
  CONCERT_DOCUMENT_TYPE_MESSAGE,
  ConcertDocumentValidationError,
  concertDocumentUploadBody,
} from "./concert-document-upload"
import type { BatteryConcertApplicationStatusDto } from "../dto/battery-concert-application.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/** Mirrors the server: PDF, PNG, or JPEG of at most 2 MiB. */
export const BATTERY_CONCERT_DOCUMENT_MAX_BYTES = CONCERT_DOCUMENT_MAX_BYTES
export const BATTERY_CONCERT_DOCUMENT_ACCEPT = CONCERT_DOCUMENT_ACCEPT
export const BATTERY_CONCERT_DOCUMENT_TYPE_MESSAGE =
  CONCERT_DOCUMENT_TYPE_MESSAGE

export { ConcertDocumentValidationError as BatteryConcertDocumentValidationError }

/**
 * Upload or replace one underwriting document. Identity comes from the
 * stipulation id; the server resolves the Concert requirement itself.
 */
export default function useUploadBatteryConcertDocument(): UseMutationResult<
  BatteryConcertApplicationStatusDto,
  unknown,
  {
    proposalId: string
    stipulationId: string
    requirementId: string
    file: File
  }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ proposalId, stipulationId, requirementId, file }) =>
      batteryProposalService.uploadConcertDocument(
        proposalId,
        stipulationId,
        await concertDocumentUploadBody(file, requirementId),
      ),
    onSuccess: (status, { proposalId }) => {
      applyBatteryConcertApplicationStatus(queryClient, proposalId, status)
    },
    onError: (error, { proposalId }) => {
      if (error instanceof ConcertDocumentValidationError) {
        return
      }
      invalidateBatteryConcertApplication(queryClient, proposalId)
    },
  })
}
